// apps/storefront/src/features/cart/cart-sync-engine.ts
// ─────────────────────────────────────────────────────────────────
// Motor del sync del carrito local (Zustand) con el backend, sin React:
// useCartSync lo monta y le pasa el store, la API y los eventos de ventana.
//
// Reglas (docs/architecture/STOREFRONT_CART_SYNC.md §6 y
// STOREFRONT_GUEST_CART_MERGE.md §4.3):
// - El store local es el dueño de la UX: un error nunca lo vacía.
// - Al iniciar sesión (resync) se UNE el carrito local con el del server por
//   productId; en conflicto gana la cantidad local (la sesión activa, igual
//   que "guest wins" en el merge del server). El tope de stock lo aplica el
//   server en el PUT. Un carrito local con ítems nunca se reemplaza por uno
//   vacío.
// - Si falla, reintenta con backoff 2/5/15/30 s y PARA. Se retoma con la
//   próxima mutación o con `wake()` (online / pestaña visible).
// - stop() limpia todos los timers y suscripciones.
// ─────────────────────────────────────────────────────────────────

import type { CartItem } from "./store";
import type { ServerCartDto, ServerCartItemDto } from "./api";

export const SYNC_DEBOUNCE_MS = 1_200;
export const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000] as const;

export type CartSyncDeps = {
  getItems: () => CartItem[];
  setItems: (items: CartItem[]) => void;
  /** Avisa cada cambio de `items` del store. Devuelve el unsubscribe. */
  subscribe: (onItemsChange: () => void) => () => void;
  fetchServerCart: () => Promise<ServerCartDto>;
  syncServerCart: (
    items: Array<{ productId: string; quantity: number }>,
  ) => Promise<ServerCartDto>;
  /** Hay identidad (Bearer o guestSessionId) para hablar con el server. */
  canSync: () => boolean;
  /** Registra `online` / `visibilitychange`. Devuelve el cleanup. */
  listenWake?: (onWake: () => void) => () => void;
};

export type CartSyncEngine = {
  start: () => void;
  stop: () => void;
  /** Post-login: el server ya hizo el merge guest→customer. */
  resync: () => void;
  wake: () => void;
};

type Task = "initial" | "resync" | "push";

/** Convierte un item del server al formato del store local */
export function serverItemToLocal(item: ServerCartItemDto): CartItem {
  return {
    productId: item.productId,
    slug: item.slug,
    name: item.name,
    price: item.price,
    qty: item.quantity,
    maxStock: item.maxStock,
    imageUrl: item.imageUrl,
    currency: item.currency,
    // Sprint 12 Slice 3 — propagar flags wholesale para el indicador.
    isWholesaleApplied: item.isWholesaleApplied,
    wholesaleMinQty: item.wholesaleMinQty,
    qtyToUnlock: item.qtyToUnlock,
  };
}

/**
 * Unión local ∪ server por productId. En conflicto gana la cantidad local;
 * precio, stock y flags vienen del server. Lo que solo está en el server
 * (y disponible) se agrega. Nunca devuelve menos productos que `local`.
 */
export function unionCartItems(
  local: CartItem[],
  server: ServerCartItemDto[],
): CartItem[] {
  const serverMap = new Map(
    server.filter((s) => s.available).map((s) => [s.productId, s]),
  );
  const localIds = new Set(local.map((l) => l.productId));

  const merged = local.map((l) => {
    const s = serverMap.get(l.productId);
    return s ? { ...serverItemToLocal(s), qty: l.qty } : l;
  });
  for (const s of serverMap.values()) {
    if (!localIds.has(s.productId)) merged.push(serverItemToLocal(s));
  }
  return merged;
}

function qtyKey(items: Array<{ productId: string; qty?: number; quantity?: number }>) {
  return items
    .map((i) => `${i.productId}:${i.qty ?? i.quantity}`)
    .sort()
    .join("|");
}

export function createCartSyncEngine(deps: CartSyncDeps): CartSyncEngine {
  let stopped = false;
  let initialDone = false;
  let fetching = false;
  let pushing = false;
  let pendingPush = false;
  /** true mientras el motor escribe el store (no es una mutación del usuario). */
  let applying = false;
  let failedTask: Task | null = null;
  let retryAttempt = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let unsubscribe: (() => void) | null = null;
  let unlistenWake: (() => void) | null = null;

  function apply(items: CartItem[]) {
    applying = true;
    try {
      deps.setItems(items);
    } finally {
      applying = false;
    }
  }

  function clearRetry() {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
    retryAttempt = 0;
  }

  function succeeded() {
    failedTask = null;
    clearRetry();
  }

  function fail(task: Task) {
    failedTask = task;
    if (retryTimer || retryAttempt >= RETRY_DELAYS_MS.length) return; // agotado: espera mutación o wake
    const delay = RETRY_DELAYS_MS[retryAttempt++];
    retryTimer = setTimeout(() => {
      retryTimer = null;
      run(task);
    }, delay);
  }

  function run(task: Task) {
    if (stopped || !deps.canSync()) return;
    if (task === "push") void push();
    else void fetchAndReconcile(task);
  }

  async function fetchAndReconcile(task: "initial" | "resync") {
    if (fetching && task === "initial") return;
    fetching = true;
    try {
      const serverCart = await deps.fetchServerCart();
      if (stopped) return;
      initialDone = true;
      succeeded();

      const local = deps.getItems();
      if (task === "initial") {
        if (local.length === 0 && serverCart.items.length > 0) {
          // Local vacío, server con datos → adoptar server
          apply(serverCart.items.filter((i) => i.available).map(serverItemToLocal));
        } else if (local.length > 0) {
          // Local con datos → local gana (sesión activa), push al server
          void push();
        }
        return;
      }

      const merged = unionCartItems(local, serverCart.items);
      if (JSON.stringify(merged) !== JSON.stringify(local)) apply(merged);
      if (qtyKey(merged) !== qtyKey(serverCart.items)) void push();
    } catch {
      if (!stopped) fail(task);
    } finally {
      fetching = false;
    }
  }

  async function push() {
    if (stopped || !deps.canSync()) return;
    if (pushing) {
      pendingPush = true;
      return;
    }
    pushing = true;
    pendingPush = false;

    const sent = new Map(deps.getItems().map((i) => [i.productId, i.qty]));
    try {
      const serverCart = await deps.syncServerCart(
        [...sent].map(([productId, quantity]) => ({ productId, quantity })),
      );
      if (stopped) return;
      succeeded();

      // Actualizar maxStock y precios desde el server (source of truth). Lo
      // que el usuario cambió mientras el PUT viajaba se respeta: lo manda
      // el push pendiente.
      const serverMap = new Map(serverCart.items.map((si) => [si.productId, si]));
      let changed = false;
      const updated = deps
        .getItems()
        .map((local) => {
          if (!sent.has(local.productId)) return local; // agregado durante el PUT
          const server = serverMap.get(local.productId);
          if (!server) {
            // Producto purgado por el server → eliminar localmente
            changed = true;
            return null;
          }
          const qty =
            local.qty === sent.get(local.productId) ? server.quantity : local.qty;
          const needsUpdate =
            local.maxStock !== server.maxStock ||
            local.price !== server.price ||
            local.qty !== qty ||
            // Sprint 12 Slice 3 — re-render si cambian los flags wholesale.
            local.isWholesaleApplied !== server.isWholesaleApplied ||
            local.wholesaleMinQty !== server.wholesaleMinQty ||
            local.qtyToUnlock !== server.qtyToUnlock;
          if (!needsUpdate) return local;
          changed = true;
          return {
            ...local,
            price: server.price,
            maxStock: server.maxStock,
            qty,
            imageUrl: server.imageUrl ?? local.imageUrl,
            currency: server.currency ?? local.currency,
            isWholesaleApplied: server.isWholesaleApplied,
            wholesaleMinQty: server.wholesaleMinQty,
            qtyToUnlock: server.qtyToUnlock,
          };
        })
        .filter((x): x is CartItem => x !== null);

      if (changed) apply(updated);
    } catch {
      // El store local queda intacto; se reintenta.
      pendingPush = false;
      if (!stopped) fail("push");
    } finally {
      pushing = false;
      if (pendingPush && !stopped) void push();
    }
  }

  function onItemsChange() {
    if (applying || stopped) return;
    // Mutación del usuario: retoma un sync caído desde el primer intento.
    clearRetry();
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      run(failedTask === "resync" ? "resync" : initialDone ? "push" : "initial");
    }, SYNC_DEBOUNCE_MS);
  }

  function wake() {
    if (stopped || failedTask === null) return;
    const task = failedTask;
    clearRetry();
    run(task);
  }

  return {
    start() {
      if (stopped || unsubscribe) return;
      unsubscribe = deps.subscribe(onItemsChange);
      unlistenWake = deps.listenWake?.(wake) ?? null;
      run("initial");
    },
    stop() {
      stopped = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = null;
      clearRetry();
      unsubscribe?.();
      unlistenWake?.();
      unsubscribe = null;
      unlistenWake = null;
    },
    resync() {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = null;
      clearRetry();
      run("resync");
    },
    wake,
  };
}
